import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Plus, Upload } from 'lucide-react'

import {
  deleteScriptDocument,
  getProduction,
  listScriptDocuments,
  type Production,
  type ScriptDocument,
} from '@/api/studio-production'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { CatalogListSkeleton } from '@/shared/components/catalog/CatalogListSkeleton'
import { CatalogListToolbar, CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { PageEmptyState, PageEmptyStateActionButton } from '@/shared/components/PageEmptyState'
import { ScriptDocumentModal } from '@/features/studio/modals/ScriptDocumentModal'
import { ScriptImportModal } from '@/features/studio/modals/ScriptImportModal'
import { ScriptDocumentCard } from '@/features/studio/components/ScriptDocumentCard'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBadge, ProtoBtn, ProtoPagination, ScriptDeleteBtn, ScriptMetaEditBtn, ScriptWriteLink } from '@/features/studio/components/ui-kit'

const SCRIPT_LIST_PAGE_SIZE = 12

function scriptDisplayName(
  doc: ScriptDocument,
  index: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const title = doc.title?.trim()
  if (title) return title
  return t('script.unnamedScript', { n: index + 1 })
}

export function ScriptListPage() {
  const { productionId = '' } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const base = productionBasePath(productionId)

  const [production, setProduction] = useState<Production | null>(null)
  const [items, setItems] = useState<ScriptDocument[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ScriptDocument | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [metaOpen, setMetaOpen] = useState(false)
  const [metaEditing, setMetaEditing] = useState<ScriptDocument | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [importScriptId, setImportScriptId] = useState<number | null>(null)

  const canManageScripts =
    production?.is_owner ||
    production?.role === 'producer' ||
    production?.role === 'executive_producer' ||
    production?.role === 'director'

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    const offset = (page - 1) * SCRIPT_LIST_PAGE_SIZE
    try {
      const [prod, listRes] = await Promise.all([
        getProduction(productionId),
        listScriptDocuments(productionId, { limit: SCRIPT_LIST_PAGE_SIZE, offset }),
      ])
      setProduction(prod)
      setItems(listRes.items)
      setTotal(listRes.total)
      setImportScriptId((prev) => {
        if (prev != null && listRes.items.some((d) => d.id === prev)) return prev
        return listRes.items[0]?.id ?? prev
      })
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'script.loadEpisodesFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, page])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / SCRIPT_LIST_PAGE_SIZE))
    if (page > maxPage) setPage(maxPage)
  }, [total, page])

  async function handleDeleteScript() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    setDeleteError(null)
    try {
      await deleteScriptDocument(productionId, deleteTarget.id)
      setDeleteTarget(null)
      await reload()
    } catch (e) {
      setDeleteError(resolveApiErrorMessage(e, 'script.deleteScriptFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  function scriptStatusBadge(doc: ScriptDocument) {
    if (doc.status === 'finalized') {
      return (
        <ProtoBadge tone="sky">
          {t('script.statusFinalized', { rev: doc.current_revision ?? 1 })}
        </ProtoBadge>
      )
    }
    return (
      <ProtoBadge tone={doc.has_body ? 'amber' : 'default'}>
        {doc.has_body ? t('script.statusDraft') : t('script.statusEmpty')}
      </ProtoBadge>
    )
  }

  function openMetaModal(doc: ScriptDocument | null) {
    setMetaEditing(doc)
    setMetaOpen(true)
  }

  function openEditor(scriptId: number) {
    navigate(`${base}/script/${scriptId}/episodes/1`)
  }

  return (
    <>
      <CatalogListPageShell>
        <CatalogListToolbar
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <ProtoBtn
                size="sm"
                variant="secondary"
                disabled={importScriptId == null}
                onClick={() => setImportOpen(true)}
              >
                <Upload className="h-3.5 w-3.5" />
                {t('script.importTitle')}
              </ProtoBtn>
              {canManageScripts ? (
                <CatalogListPrimaryButton disabled={loading} onClick={() => openMetaModal(null)}>
                  <Plus className="h-4 w-4" />
                  {t('script.addScript')}
                </CatalogListPrimaryButton>
              ) : null}
            </div>
          }
        />

        {error ? (
          <p className="mb-4 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-2 text-sm text-red-300">
            {error}
          </p>
        ) : null}

        {loading ? (
          <CatalogListSkeleton />
        ) : total === 0 ? (
        <PageEmptyState icon={FileText} tone="amber" title={t('script.scriptsEmptyTitle')} subtitle={t('script.scriptsEmpty')}>
          {canManageScripts ? (
            <PageEmptyStateActionButton onClick={() => openMetaModal(null)}>
              <Plus className="h-4 w-4" />
              {t('script.addScript')}
            </PageEmptyStateActionButton>
          ) : null}
        </PageEmptyState>
      ) : (
        <>
          <ul className={CATALOG_LIST_GRID_CLASS}>
            {items.map((doc, rowIndex) => {
              const globalIndex = (page - 1) * SCRIPT_LIST_PAGE_SIZE + rowIndex
              return (
                <ScriptDocumentCard
                  key={doc.id}
                  doc={doc}
                  index={globalIndex}
                  href={`${base}/script/${doc.id}/episodes/1`}
                  statusBadge={
                    <div className="flex flex-wrap gap-1">
                      {scriptStatusBadge(doc)}
                      {doc.has_outline ? (
                        <ProtoBadge tone="violet" className="!text-[10px]">
                          {t('script.hasOutline')}
                        </ProtoBadge>
                      ) : null}
                    </div>
                  }
                  bottomActions={
                    canManageScripts ? (
                      <>
                        <ScriptMetaEditBtn onClick={() => openMetaModal(doc)}>{t('script.editMeta')}</ScriptMetaEditBtn>
                        <ScriptWriteLink to={`${base}/script/${doc.id}/episodes/1`}>
                          {t('script.writeScript')}
                        </ScriptWriteLink>
                        {total > 1 ? (
                          <ScriptDeleteBtn
                            onClick={() => {
                              setDeleteError(null)
                              setDeleteTarget(doc)
                            }}
                          >
                            {t('script.deleteScript')}
                          </ScriptDeleteBtn>
                        ) : null}
                      </>
                    ) : (
                      <ScriptWriteLink to={`${base}/script/${doc.id}/episodes/1`}>
                        {t('script.writeScript')}
                      </ScriptWriteLink>
                    )
                  }
                />
              )
            })}
          </ul>
          <ProtoPagination
            page={page}
            total={total}
            pageSize={SCRIPT_LIST_PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      )}
      </CatalogListPageShell>

      <ScriptDocumentModal
        open={metaOpen}
        onClose={() => {
          setMetaOpen(false)
          setMetaEditing(null)
        }}
        productionId={productionId}
        production={production}
        editing={metaEditing}
        onSaved={() => void reload()}
        onWrite={openEditor}
      />

      {importScriptId != null ? (
        <ScriptImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          productionId={productionId}
          scriptId={importScriptId}
          onApplied={() => void reload()}
        />
      ) : null}

      <ConfirmDialog
        open={deleteTarget != null}
        title={t('script.deleteScriptTitle')}
        message={
          deleteTarget
            ? t('script.deleteScriptConfirm', {
                name: scriptDisplayName(
                  deleteTarget,
                  Math.max(0, items.findIndex((x) => x.id === deleteTarget.id)),
                  t,
                ),
              })
            : ''
        }
        confirmLabel={t('script.deleteScript')}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void handleDeleteScript()}
        onCancel={() => {
          if (deleteBusy) return
          setDeleteTarget(null)
          setDeleteError(null)
        }}
      />
    </>
  )
}
